<template>
	<div class="form-item" :class="{ success, error }">
		<div class="form-item-header">
			<label class="form-item-label" :for="id" :id="formItemId">
				<slot name="label">{{ label }}</slot>
			</label>
			<div class="form-item-alert">
				<slot name="alert-icon">
					<CircleExclamationIcon v-if="error || success" />
				</slot>
			</div>
		</div>
		<div class="form-item-content" ref="content">
			<slot />
		</div>
		<div class="form-item-footer">
			<div class="form-item-text">
				<div class="form-item-message" v-if="error || success">
					<slot name="message">
						{{ message }}
					</slot>
				</div>
				<div class="form-item-help">
					<slot name="help">
						{{ help }}
					</slot>
				</div>
			</div>
			<div class="form-item-count" v-if="showCount">
				<slot name="count"> {{ count }}/{{ max }} </slot>
			</div>
		</div>
	</div>
</template>

<script>
import { CircleExclamationIcon } from '@/icons'

const classes = {
	SUCCESS: 'form-item-success',
	ERROR: 'form-item-error',
}

export default {
	name: 'FormItem',
	components: { CircleExclamationIcon },
	props: {
		label: String,
		help: String,
		message: String,
		count: Number,
		max: Number,
		success: { type: Boolean, default: null },
		error: { type: Boolean, default: null },
	},
	provide() {
		return {
			labelId: this.id,
			formItemId: this.formItemId,
		}
	},
	data() {
		return { childElement: null }
	},
	mounted() {
		this.startObservingChild()
		this.setChildClasses()
	},
	beforeUnmount() {
		this.classObserver?.disconnect()
	},
	computed: {
		showCount() {
			return Number.isInteger(this.count)
		},
		id() {
			return this._.uid
		},
		formItemId() {
			return `form_item_${this.id}`
		},
	},
	watch: {
		// NOTE: Every component that uses FormItem is responsible for
		// implementing its own styles for the .form-item-success and
		// .form-item-error classes
		success(success) {
			this.setContentClass(success, classes.SUCCESS)
		},
		error(error) {
			this.setContentClass(error, classes.ERROR)
		},
		childElement(elem, prev) {
			if (elem && !prev) {
				this.startObservingClasses()
			}
		},
	},
	methods: {
		isChildCorrect() {
			if (!this.childElement) return false
			const { classList } = this.childElement
			if (!classList) return
			if (this.success && !classList.contains(classes.SUCCESS)) {
				return false
			}
			if (this.error && !classList.contains(classes.ERROR)) {
				return false
			}
			return true
		},
		setChildClasses() {
			if (this.isChildCorrect()) return
			this.setContentClass(this.success, classes.SUCCESS)
			this.setContentClass(this.error, classes.ERROR)
		},
		setContentClass(shouldAdd, className) {
			if (!this.childElement) return
			const { classList } = this.childElement
			if (!classList) return
			if (shouldAdd && !classList.contains(className)) {
				classList.add(className)
				return
			}
			if (!shouldAdd && classList.contains(className)) {
				classList.remove(className)
				return
			}
		},
		startObservingClasses() {
			if (!this.childElement || this.classObserver) return
			this.classObserver = new MutationObserver((mutations) => {
				const m = mutations[0]
				if (m.type === 'attributes' && m.attributeName === 'class') {
					this.setChildClasses()
				}
			})
			this.classObserver.observe(this.childElement, { attributes: true })
		},
		startObservingChild() {
			if (this.childObserver || this.childElement) return
			if (this.$refs.content?.firstElementChild) {
				this.childElement = this.$refs.content?.firstElementChild
				return
			}
			this.childObserver = new MutationObserver((mutations) => {
				const m = mutations[0]
				if (m.type === 'childList' && m.target.firstChild) {
					this.childElement = m.target.firstChild
					this.childObserver.disconnect()
				}
			})
			this.childObserver.observe(this.$refs.content, { childList: true })
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.form-item {
	width: 100%;
	@include flex-column;
	& + .form-item {
		// if stacking multiple form items, add spacing between them
		margin-top: 16px;
	}
	&.success {
		.form-item-header {
			.form-item-alert {
				color: var(--teal-300);
			}
		}
		.form-item-message {
			color: var(--teal-300);
		}
	}
	&.error {
		.form-item-header {
			.form-item-alert {
				color: var(--red-200);
			}
		}
		.form-item-message {
			color: var(--red-200);
		}
	}
	.form-item-header {
		@include flex-spread;
		margin-bottom: 8px;
		.form-item-label {
			font-family: Avenir-Black;
			font-size: 14px;
			color: var(--neutral-800);
		}
		.form-item-alert {
			height: 1em;
			font-size: 18px;
		}
	}
	.form-item-footer {
		margin-top: 4px;
		.form-item-help {
			font-size: 14px;
			color: var(--neutral-400);
		}
		.form-item-message {
			font-size: 14px;
			font-family: Avenir-Heavy;
		}
	}
}
</style>
